// @givmo/org-console — the module tile states, decided once for every host (OM-6 + BM-HK1).
//
// ══ WHY THE VERDICT LIVES HERE AND THE REGISTRY DOES NOT ═══════════════════════════════════════
//
// A host's module REGISTRY is data — which tiles it knows, in which order, behind which build-time
// URL variable — and it stays a per-host file, duplicated and byte-pinned across the pair of apps
// that share one grid. The VERDICT is not data: it is one rule about authority, and a second copy of
// it is a second answer to "may this person open that". Two hosts already carried the tolerant
// readers and the somewhere-to-go predicate in unpinned files, and a third state landing in each
// would have been the drift the registry's own pin exists to prevent — this time across hosts that
// cannot be pinned against each other at all, because their module lists differ.
//
// So the rule is written once, over a NORMALIZED per-module input, and each host adapts its own wire
// into it. This package is the home because it is already the one place the `/org` authority is read.
//
// ══ THE FIVE STATES, AND THEIR PRECEDENCE ══════════════════════════════════════════════════════
//
//   absent > (off | setting-up) > locked > link
//
//   (A TILE has a sixth, OM-33's `coming-soon`, between absent and off — see `tileVerdictFor` below.
//   It is never a standing, so nothing but the square derivation sees it.)
//
//   ABSENT — the organization's class does not carry this module at all. No tile. Nobody here can
//            grant it, so a tile pointing at an admin would send a person to someone who cannot help.
//   OFF    — the organization has the module switched off. Beats a missing grant, and the order
//            matters: an admin with no grant on a switched-off module reads as OFF, never as LOCKED
//            — the organization deliberately turned it off, and the person who can flip that switch
//            is offered "Turn on" while everyone else reads the same no-access line.
//   SETTING-UP — the organization's provisioning hook is still doing real work. Like OFF, this beats
//                a missing grant: setup truth is org-wide and a request would name the wrong
//                decision. It is never openable module content.
//   LOCKED     — offered to this organization, not held by this person. The tile says so and, when
//                the host wires a request door, offers "Request access" (shell contract v3).
//   LINK       — held, and switched on. An ordinary square.
//
// ⚠ NOT REPORTED IS PERMISSIVE, AND IT HIDES THE FOURTH STATE ON PURPOSE. A read in flight, a read
// that errored, or a host talking to a backend that predates the fields all arrive as `null` — and
// the answer is then every built tile as an ordinary link. Hiding someone's apps because a request
// was slow is a worse answer than a tile whose destination refuses them. The consequence is stated
// rather than hidden: while nothing is reported, an org-off module is INVISIBLE as off. That is
// correct, it is also the whole pre-reveal posture of a host whose org read is still dark, and it is
// not a defect to be fixed.

/**
 * One module's standing, normalized away from any host's wire.
 *
 * Every field is a POSITIVE fact the caller's own read asserted. There is no "unknown" member: a read
 * that reported nothing hands `null` to {@link verdictFor} instead, which is a different question
 * with a different answer.
 */
export type ModuleActivationState = 'on' | 'setting-up' | 'off';

/** One bounded, organization-visible step returned by a provisioning hook. */
export interface ModuleActivationAction {
  key: string;
  message: string;
}

/** Curated organization-visible refusal/precondition data. Raw transport text never enters this. */
export interface ModuleActivationReason {
  code: string;
  message: string;
  actions: ModuleActivationAction[];
}

/**
 * Normalize the backend's additive activation state once.
 *
 * Exact string membership is load-bearing: truthy-coercing an unknown future or malformed value
 * would turn a shape this client does not understand into authority. `null` means "use the legacy
 * enabled boolean exactly as before this field existed".
 */
export function normalizeModuleActivationState(value: unknown): ModuleActivationState | null {
  if (value === 'on' || value === 'setting-up' || value === 'off') return value;
  return null;
}

const ACTIVATION_REASON_CODE = /^[A-Z][A-Z0-9_]*$/;
const ACTIVATION_ACTION_KEY = /^[a-z][a-z0-9_]*$/;

function boundedText(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 && text.length <= maxLength ? text : null;
}

/**
 * Normalize only the curated, bounded reason contract the Modules-page switch may render.
 *
 * This type is deliberately NOT a member of {@link ModuleStanding}. Tiles and direct-link gates can
 * learn the three-valued state but structurally cannot receive or render a refusal reason; the
 * switch that started the work is its only presentation surface.
 */
export function normalizeModuleActivationReason(value: unknown): ModuleActivationReason | null {
  if (!isRecord(value)) return null;
  const code = boundedText(value.code, 80);
  const message = boundedText(value.message, 320);
  if (code === null || !ACTIVATION_REASON_CODE.test(code) || message === null) return null;

  const actions: ModuleActivationAction[] = [];
  if (Array.isArray(value.actions)) {
    for (const candidate of value.actions.slice(0, 16)) {
      if (!isRecord(candidate)) continue;
      const key = boundedText(candidate.key, 64);
      const actionMessage = boundedText(candidate.message, 240);
      if (key === null || !ACTIVATION_ACTION_KEY.test(key) || actionMessage === null) continue;
      actions.push({ key, message: actionMessage });
    }
  }
  return { code, message, actions };
}

/** The four states an access request can be in — the store's own vocabulary, read as literals. */
export type ModuleAccessRequestStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'VOID';

/**
 * The caller's OWN latest access request on one module, as the standing read carries it (the
 * additive `access_request` sub-object). A person's request fact DECORATES the tile — "Access
 * requested", "Request declined" — and never reorders the five-verdict precedence.
 */
export interface ModuleAccessRequest {
  id: string;
  status: ModuleAccessRequestStatus;
  /** The tier asked for — the module's own `Owner` | `Member` vocabulary, never the org base role. */
  requestedRole: string;
  createdAt: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
}

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Normalize the additive `access_request` sub-object, or `null` for anything unreadable.
 *
 * ⚠ AN UNKNOWN STATUS DROPS THE WHOLE REQUEST rather than guessing one. A tile decorated with the
 * wrong request state would either hide a real "Request access" link or claim a request nobody
 * made; a tile that shows no request fact is merely the pre-request reading, which is always true.
 */
export function normalizeModuleAccessRequest(value: unknown): ModuleAccessRequest | null {
  if (!isRecordValue(value)) return null;
  const id = stringOrNull(value.id);
  const status = value.status;
  if (
    id === null ||
    (status !== 'PENDING' && status !== 'APPROVED' && status !== 'DENIED' && status !== 'VOID')
  ) {
    return null;
  }
  return {
    id,
    status,
    requestedRole: stringOrNull(value.requested_role) ?? '',
    createdAt: stringOrNull(value.created_at),
    resolvedAt: stringOrNull(value.resolved_at),
    resolutionNote: stringOrNull(value.resolution_note),
  };
}

export interface ModuleStanding {
  /** The organization's class carries this module at all. Key-presence on a reported map. */
  known: boolean;
  /** This person holds the module. */
  held: boolean;
  /** The organization has the module switched ON. */
  orgEnabled: boolean;
  /** A recognized provisioning-hook projection. Missing/malformed means the legacy boolean wins. */
  activationState?: ModuleActivationState;
  /**
   * Whether the organization MAY switch this module at all.
   *
   * ⚠ ABSENT READS AS `true`. The always-on activation mode arrives as an additive
   * `switchable: false` on the row it applies to; every ordinary row, and every row from a backend
   * that predates the field, says nothing and is switchable exactly as before.
   */
  switchable?: boolean;
  /** The caller's own latest access request on this module, when the read carried one. */
  accessRequest?: ModuleAccessRequest;
  /**
   * How many requests for THIS module are waiting on the caller — present only when the read
   * reported it, which the backend does only for a holder of that module's approval string.
   */
  pendingAccessRequests?: number;
}

/** Every module a read reported, keyed by module slug. */
export type ModuleStandings = Readonly<Record<string, ModuleStanding>>;

/** How one module's tile renders. See the header for the precedence and why it is that way. */
export type ModuleVerdict = 'absent' | 'off' | 'setting-up' | 'locked' | 'link';

/**
 * The rule, over one normalized standing.
 * **Precedence: absent > (off | setting-up) > locked > link.**
 *
 * Written as early returns in precedence order so the order IS the code — a reader checking the
 * contract against this function is checking the ordered early returns, not a boolean expression.
 */
export function moduleVerdict(standing: ModuleStanding): ModuleVerdict {
  if (!standing.known) return 'absent';
  if (standing.activationState === 'off') return 'off';
  if (standing.activationState === 'setting-up') return 'setting-up';
  // A recognized ON is newer and more specific than a conflicting legacy boolean. Missing or
  // malformed state keeps the legacy answer exactly as it was before BM-HK1.
  if (standing.activationState !== 'on' && !standing.orgEnabled) return 'off';
  if (!standing.held) return 'locked';
  return 'link';
}

/**
 * The verdict for one module, given what a read reported.
 *
 * `standings === null` means NOT REPORTED and answers `link` for everything — see the header. A slug
 * missing from a REPORTED map is `absent`, which is the whole reason key-presence is carried.
 */
export function verdictFor(standings: ModuleStandings | null, slug: string): ModuleVerdict {
  if (standings === null) return 'link';
  const standing = standings[slug];
  return standing === undefined ? 'absent' : moduleVerdict(standing);
}

// ── The sixth TILE state: coming soon (Org Machinery OM-33) ───────────────────────────────────
//
// A module the organization's class carries, that Givmo has ANNOUNCED for the class and released,
// but that THIS organization is not offered, appears in the app popup as a muted "Coming soon"
// tile: no link, no request door, no lock — it is not about the person's access. The standing read
// names such modules in its `coming_soon_modules` list (`StandingRead.comingSoonModules`).
//
// ⚠ IT IS A TILE STATE, NEVER A STANDING. The organization is not offered the module, so it is
// ABSENT from everything the organization may do: the direct-link gate, the sidebar, the landing
// and the settings page keep reading it as absent through `verdictFor`, and only the square
// derivation consults the list (`tileVerdictFor`).

/** A module's TILE: the five verdicts plus OM-33's `coming-soon`. */
export type ModuleTileVerdict = ModuleVerdict | 'coming-soon';

/**
 * The rule for a TILE. **Precedence: absent > coming-soon > (off | setting-up) > locked > link.**
 *
 * `absent` outranks the announcement where it is decided: a module this build has no address for is
 * dropped before any verdict is read (`deriveConsoleSquares`), and the server names only modules the
 * class carries. Past that, a coming-soon module is coming soon whatever a standing says — a module
 * the organization is not offered cannot also be off, locked or openable for it.
 */
export function tileVerdictFor(
  standings: ModuleStandings | null,
  slug: string,
  comingSoon: readonly string[],
): ModuleTileVerdict {
  if (comingSoon.includes(slug)) return 'coming-soon';
  return verdictFor(standings, slug);
}

// ── The tolerant readers ─────────────────────────────────────────────────────
//
// Two wires say the same three things in different words, and both arrive as `unknown`: one host's
// per-module map is an OPEN bag the generator types as `{ [key: string]: unknown }`, and the org
// self read's list is modelled here but still crosses a boundary this package does not control.
//
// ⚠ EVERY BOOLEAN IS READ AS `=== true`, and on one of them that direction is inverted DELIBERATELY.
// `held` guesses NO on an unrecognized shape, because guessing yes grants access. The org switch
// guesses ON, because a host whose backend has not started reporting it at all would otherwise show
// every tile as switched off — turning a missing field into a product-wide outage. Absence of the
// switch is the same statement as absence of the whole map: nothing was reported about it.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The key an open per-module bag carries the org switch under. */
const ENABLED_KEY = 'enabled_for_company';

/**
 * Normalize an OPEN per-module bag — `{ <slug>: { has_access, enabled_for_… } }` — into standings.
 *
 * Returns `null` for anything that is not a non-empty object: `null`, `undefined`, `{}`, an array, a
 * string. All of them mean NOT REPORTED, which {@link verdictFor} answers permissively.
 *
 * ⚠ THE SWITCH FIELD MAY BE ABSENT, AND THAT IS A SUPPORTED STATE, NOT A BUG. A backend deployed
 * before it was serialized reports the grant and nothing else; that entry reads as switched ON, so
 * the tile falls through to the grant answer exactly as it did before the field existed. The client
 * therefore works unchanged against both sides of that deploy.
 */
export function standingsFromAccessBag(value: unknown): ModuleStandings | null {
  if (!isRecord(value)) return null;
  const slugs = Object.keys(value);
  if (slugs.length === 0) return null;
  const standings: Record<string, ModuleStanding> = {};
  for (const slug of slugs) {
    const entry = value[slug];
    const record = isRecord(entry) ? entry : null;
    const activationState = normalizeModuleActivationState(record?.activation_state);
    const legacyEnabled =
      record === null || !(ENABLED_KEY in record) || record[ENABLED_KEY] === true;
    standings[slug] = {
      known: true,
      held: record?.has_access === true,
      // Absent ⇒ ON. See the header note above.
      orgEnabled: activationState === null ? legacyEnabled : activationState === 'on',
      ...(activationState === null ? {} : { activationState }),
      ...additiveStandingFacts(record),
    };
  }
  return standings;
}

/**
 * The three additive facts BOTH readers carry the same way — the always-on `switchable`, the
 * caller's own `access_request`, and the per-module pending count for an approver.
 *
 * Each is OMITTED rather than defaulted when the wire says nothing: `switchable` absent means the
 * ordinary switchable reading, a missing request is the pre-request reading, and a missing count
 * is "not an approver here". Emitting a default would turn an older backend's silence into a claim.
 */
function additiveStandingFacts(
  record: Record<string, unknown> | null,
): Pick<ModuleStanding, 'switchable' | 'accessRequest' | 'pendingAccessRequests'> {
  if (record === null) return {};
  const accessRequest = normalizeModuleAccessRequest(record.access_request);
  return {
    // Read as `=== false`, the one boolean here whose unrecognized shape resolves to the PERMISSIVE
    // reading on purpose: an unswitchable module is the rarer, newer fact, and guessing it would
    // hide a real switch from the person who owns it.
    ...(record.switchable === false ? { switchable: false } : {}),
    ...(accessRequest === null ? {} : { accessRequest }),
    ...(typeof record.pending_access_requests === 'number' &&
    Number.isInteger(record.pending_access_requests) &&
    record.pending_access_requests >= 0
      ? { pendingAccessRequests: record.pending_access_requests }
      : {}),
  };
}

/** The key the org-generic module row carries the switch under. */
const ORG_ENABLED_KEY = 'enabled_for_org';

/**
 * Normalize the org self read's `modules` LIST into standings.
 *
 * The list is the org-generic shape — one row per module the organization's class carries, each row
 * naming the module and carrying both booleans — so presence in the list IS `known`. An empty or
 * non-array value is NOT REPORTED.
 */
export function standingsFromModuleRows(value: unknown): ModuleStandings | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const standings: Record<string, ModuleStanding> = {};
  for (const row of value) {
    if (!isRecord(row)) continue;
    const slug = typeof row.module === 'string' ? row.module.trim() : '';
    if (slug.length === 0) continue;
    const activationState = normalizeModuleActivationState(row.activation_state);
    const legacyEnabled = !(ORG_ENABLED_KEY in row) || row[ORG_ENABLED_KEY] === true;
    standings[slug] = {
      known: true,
      held: row.has_access === true,
      orgEnabled: activationState === null ? legacyEnabled : activationState === 'on',
      ...(activationState === null ? {} : { activationState }),
      ...additiveStandingFacts(row),
    };
  }
  return Object.keys(standings).length === 0 ? null : standings;
}

// ── Turning a module ON: the switch, or a provisioning hook ──────────────────
//
// Most modules turn on by flipping a grant: the org-wide settings write lands, the answer changes,
// done. A module MAY instead declare that turning it on requires SETUP WORK (D-CPO-26 R-1,
// 2026-08-30) — the switch STARTS that work, the module is on only once it COMPLETES, and a failure
// returns it to off rather than leaving it half-on. The contract is
// `agent_docs/org-console-shell.md` § "Turning a module on may take WORK".
//
// This is the ONE place the question "does turning this on take work?" is answered. Books is the
// first consumer; its transport remains module-owned and is supplied at the management surface's
// Promise seam rather than imported here.

/** Modules whose switch starts provisioning work instead of writing the org-wide setting. */
export const PROVISIONING_HOOK_MODULES = ['books'] as const satisfies readonly string[];

/** How a module's switch turns it ON. */
export type ModuleTurnOn = 'settings-write' | 'provisioning-hook';

/**
 * Which mechanism turns a module on.
 *
 * `hooks` remains injectable for focused tests and for an exact future extension, but production
 * callers normally consume the shared authority unchanged.
 */
export function turnOnMechanism(
  slug: string,
  hooks: readonly string[] = PROVISIONING_HOOK_MODULES,
): ModuleTurnOn {
  return hooks.includes(slug) ? 'provisioning-hook' : 'settings-write';
}

/**
 * Whether this HOST has a truthful way to turn one module on.
 *
 * Ordinary modules always have the shared settings writer. A hook module needs an explicit
 * host-supplied handler entry; knowing that Books is a hook is shared authority, while knowing that
 * this host mounted its transport is host data. Keeping the two inputs separate prevents an OFF tile
 * from linking to an `/org` page whose corresponding switch is structurally absent.
 */
export function moduleTurnOnIsAvailable(
  slug: string,
  activationHandlers: readonly string[],
  hooks: readonly string[] = PROVISIONING_HOOK_MODULES,
): boolean {
  return turnOnMechanism(slug, hooks) === 'settings-write' || activationHandlers.includes(slug);
}

/** The host's management href only when the module has a real ON mechanism there. */
export function moduleTurnOnHref(
  slug: string,
  managementHref: string | null,
  activationHandlers: readonly string[],
  hooks: readonly string[] = PROVISIONING_HOOK_MODULES,
): string | null {
  if (managementHref === null) return null;
  return moduleTurnOnIsAvailable(slug, activationHandlers, hooks) ? managementHref : null;
}

// ── Somewhere to GO ──────────────────────────────────────────────────────────

/** The shape {@link hasOtherModules} reads. A structural subset of the design system's square. */
export interface SwitchableModule {
  /** The rendered state. `undefined` reads as an ordinary link, matching the square's own default. */
  state?: ModuleTileVerdict | undefined;
  /** True for the app the caller is already in. */
  current?: boolean | undefined;
  /**
   * The organization's own management tile, not an app (the Admin Console).
   *
   * ⚠ IT NEVER COUNTS AS SOMEWHERE TO GO. It is always a link for whoever holds one of its
   * capabilities, and counting it would make a person with nothing openable read as having an app.
   */
  adminConsole?: boolean | undefined;
}

/**
 * Whether a set of squares offers anywhere to GO — at least one openable app that is not this one.
 *
 * The app switcher is gated on this: a popup that opens onto only the app you are standing in is a
 * control that does nothing, and with every module URL undefaulted that state is reachable by
 * ordinary misconfiguration rather than only in theory. A host's own grid still renders the single
 * square — "here is your one app" is a true statement, while a switcher offering no switch is not.
 *
 * ⚠ LOCKED, OFF, AND SETTING-UP TILES DO NOT COUNT — including their actionable variants. Their
 * destinations are management doors rather than module content, so none is an app to switch to.
 */
export function hasOtherModules(modules: readonly SwitchableModule[]): boolean {
  return modules.some(
    (module) =>
      module.adminConsole !== true &&
      (module.state ?? 'link') === 'link' &&
      module.current !== true,
  );
}
